export enum SignalType {
  BUY = 'BUY',
  SELL = 'SELL',
  NEUTRAL = 'NEUTRAL',
  HOLD = 'HOLD'
}

export enum RiskLevel {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High'
}

export interface SwingPoint {
  sequence_number: number;
  type: 'Swing High' | 'Swing Low';
  price: string;
  classification: string;
  approximate_timestamp?: string;
}

export interface SwingStructureMapping {
  all_swings_identified: SwingPoint[];
  trend_pattern_summary: string;
  most_recent_swing_high: string;
  most_recent_swing_low: string;
  structure_change_analysis: {
    reversal_signal_present: boolean;
    key_level_to_break_for_uptrend?: string;
    key_level_to_break_for_downtrend?: string;
    has_higher_low_formed?: boolean;
    has_lower_high_formed?: boolean;
    reversal_confirmation_status: string;
    next_confirmation_needed: string;
  };
}

export interface EntrySignal {
  signal_type: string;
  trigger_conditions: string[];
  confirmation_criteria: string;
  stop_loss_price: string;
  take_profit_targets: string[];
  risk_reward_ratio: string;
  time_validity: string;
}

export interface SupportResistanceLevel {
  exact_price_level: string;
  strength_rating: number;
  number_of_touches: number;
  volume_characterization: string;
  reasoning: string;
  volume_validation_metrics: {
    volume_at_level: string;
    rejection_spike: string;
    confidence_score: number;
  };
  // Specific to Support
  support_type?: string;
  entry_price_long?: string;
  entry_signal?: EntrySignal;
  
  // Specific to Resistance
  entry_price_short?: string;
  entry_price_breakout?: string;
  entry_signal_short?: EntrySignal;
  entry_signal_breakout?: EntrySignal;
  
  entry_conviction: 'STRONG' | 'MEDIUM' | 'WEAK';
}

export interface TrendAnalysis {
  overall_trend_direction: 'Uptrend' | 'Downtrend' | 'Sideways';
  swing_structure: string;
  trend_strength: number;
  key_trend_lines?: string;
  volume_confirmation: string;
}

export interface DetailedAnalysisResult {
  swing_structure_mapping: SwingStructureMapping;
  support_levels: SupportResistanceLevel[];
  resistance_levels?: SupportResistanceLevel[];
  trend_analysis: TrendAnalysis;
}

export enum AnalysisMode {
  LIVE = 'LIVE',
  REPLAY = 'REPLAY',
  UPLOAD = 'UPLOAD',
  MULTI_TIMEFRAME = 'MULTI-TIMEFRAME'
}

export interface AnalysisResult {
  technicalAnalysis: string;
  signal: SignalType;
  confidenceScore: number; // 0-100
  riskLevel: RiskLevel;
  keyLevels: string[];
  symbol?: string;
  timeframe?: Timeframe;
  timestamp?: number; // When this specific analysis was generated
  detailedAnalysis?: DetailedAnalysisResult;
  mode?: AnalysisMode; // Context of the analysis
  candleTimestamp?: number; // Reference time of the data analyzed
}

export interface ChartDataPoint {
  name: string;
  value: number;
  fill: string;
}

export interface CandleData {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
  dateStr?: string; // Helper for tooltip
}

export enum AppMode {
  UPLOAD = 'UPLOAD',
  LIVE = 'LIVE'
}

export type Timeframe = '1m' | '5m' | '15m' | '30m' | '1h' | '4h' | '1d' | '1w';

export interface ChatMessage {
  role: 'user' | 'model';
  text: string;
  timestamp?: number;
}

export interface VirtualTrade {
  id: string;
  symbol: string;
  type: 'BUY' | 'SELL';
  entryPrice: number;
  exitPrice?: number;
  quantity: number;
  entryTime: number;
  exitTime?: number;
  pnl?: number;
  status: 'OPEN' | 'CLOSED';
}

export interface ReplayState {
  isActive: boolean;
  isPaused: boolean;
  speed: number; // ms per candle update (e.g. 1000 = 1 candle/sec)
  currentIndex: number;
  maxIndex: number;
}

export interface TickData {
  time: number;
  price: number;
  volume: number;
}

export interface SavedSession {
  id: string;
  timestamp: number;
  mode: AppMode;
  title: string;
  analysis: AnalysisResult; // Keeps the latest analysis for backward compatibility/lists
  analysisHistory?: AnalysisResult[]; // Stores the full history of analyses for this session
  chatHistory: ChatMessage[];
  
  // Live Mode Data
  ticker?: string;
  timeframe?: Timeframe;
  candles?: CandleData[];
  
  // Upload Mode Data
  imageBase64?: string;
  imageMimeType?: string;
}